import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MenuWidth, MenuHeight} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {JSONValue} from 'util/Types';

import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  GetDataFrameUniqueValues,
  GetDataFrameUniqueValuesVariables,
} from './__generated/GetDataFrameUniqueValues';

const MAX_UNIQUE_VALUES = 1000;
export const GET_DATA_FRAME_UNIQUE_VALUES_QUERY = gql`
  query GetDataFrameUniqueValues(
    $dataFrameId: StrictString!
    $columnSystemName: StrictString!
    $limit: StrictInt!
  ) @skipAudit @engComponent(name: DATA_FRAMES) {
    dataFrame(id: $dataFrameId) {
      ...InaccessibleInfoGraphQL
      ... on DataFrameNode {
        id
        uniqueValues(columnSystemName: $columnSystemName, limit: $limit)
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface DataFrameColumnValueEditorProps {
  buttonSize?: ButtonSize;
  config: JSONValue;
  configShape: GetPipelineConfigSpecType<'columnValue'>;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
  noValueSelectedText?: string;
  onChangeConfig: (config: JSONValue) => void;
}

export default function DataFrameColumnValueEditor({
  buttonSize,
  config,
  configShape,
  dataTestAttrs,
  disabled,
  noValueSelectedText,
  onChangeConfig,
}: DataFrameColumnValueEditorProps): JSX.Element {
  const {dataFrameId} = useStepConfigEditorContext();
  assert(
    dataFrameId != null,
    'Using DataFrameColumnValueEditor in context without access to persisted data frame'
  );

  const placeholder = noValueSelectedText ?? 'None';

  return (
    <BenchlingQuery<GetDataFrameUniqueValues, GetDataFrameUniqueValuesVariables>
      query={GET_DATA_FRAME_UNIQUE_VALUES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, columnSystemName: configShape.columnSystemName, limit: MAX_UNIQUE_VALUES}}
    >
      {({dataFrame}) => (
        <SelectDropdown
          displayName={config != null ? config : placeholder}
          menu={
            <SearchableMenu
              height={MenuHeight.MEDIUM}
              selectedValue={config}
              onSelect={(newValue) => {
                onChangeConfig(newValue);
              }}
              width={MenuWidth.FULL}
              optionDatas={assertIsAccessible(dataFrame).uniqueValues.map((value) => ({
                name: value,
                value,
              }))}
            />
          }
          buttonSize={buttonSize}
          disabled={disabled}
          dataTestAttrs={dataTestAttrs}
        />
      )}
    </BenchlingQuery>
  );
}
